import { toast } from "sonner";
import ResidentPortrait from "@/components/ResidentPortrait";
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { EXERCISE_META, BUILTIN_GAMES } from "@/data/exercises";
import MemoryMatch from "@/components/patient/MemoryMatch";
import Recognition from "@/components/patient/Recognition";
import DragMatch from "@/components/patient/DragMatch";
import TapTargets from "@/components/patient/TapTargets";
import PathTrace from "@/components/patient/PathTrace";
import PicWords from "@/components/patient/PicWords";
import Sudoku from "@/components/patient/Sudoku";
import Crossword from "@/components/patient/Crossword";
import Differences from "@/components/patient/Differences";
import QuizPlayer from "@/components/patient/QuizPlayer";
import PuzzlePlayer from "@/components/patient/PuzzlePlayer";
import BlindTestPlayer from "@/components/patient/BlindTestPlayer";
import PronunciationPlayer from "@/components/patient/PronunciationPlayer";
import BreathingPlayer from "@/components/patient/BreathingPlayer";
import ColorsPlayer from "@/components/patient/ColorsPlayer";
import SoundsPlayer from "@/components/patient/SoundsPlayer";
import GameRecommendation from "@/components/patient/GameRecommendation";
import PrintGameButton from "@/components/patient/PrintGameButton";
import KeepGameButton from "@/components/patient/KeepGameButton";
import { getKeptGames, queueSession, flushSessions, cachePatient, cachedPatient, keepBravo } from "@/lib/offline";
import PatientVoiceDialog from "@/components/patient/PatientVoiceDialog";
import SuccessScreen from "@/components/patient/SuccessScreen";
import HesitationHelper from "@/components/patient/HesitationHelper";
import { GardienBadge } from "@/components/GardienBadge";
import { gardienForCategory, getGardien, gardienLabel, GARDIENS, CATEGORY_SCENE } from "@/data/gardiens";
import { GardienAvatar } from "@/components/GardienBadge";
import FamilySongs from "@/components/patient/FamilySongs";
import SimpleMusicPlayer from "@/components/patient/SimpleMusicPlayer";
import BlindTest from "@/components/patient/BlindTest";
import WhoSaid from "@/components/patient/WhoSaid";
import MoraleQuiz from "@/components/patient/MoraleQuiz";
import VoiceRecorder from "@/components/patient/VoiceRecorder";
import FamiliarAlbum from "@/components/patient/FamiliarAlbum";
import { MiniPlay } from "@/components/FamilyVoices";
import SessionNoteForm from "@/components/patient/SessionNoteForm";
import { ScrollText, Feather, Brain, Puzzle, MessageCircle, Sparkles, Clock, ChevronLeft, ChevronRight, Loader2, Lock, CreditCard, Settings2, BookOpen, Music, HeartHandshake, Mic, Download, Headphones, Disc3 } from "lucide-react";

const CAT_STYLE = {
  alzheimer: { icon: Brain, bg: "#23739F", text: "#FFFFFF", accent: "rgba(255,255,255,0.18)" },
  autisme: { icon: Puzzle, bg: "#F8E4C7", text: "#5A3A1E", accent: "rgba(90,58,30,0.10)" },
  aphasie: { icon: MessageCircle, bg: "#DCEFE4", text: "#1F4D36", accent: "rgba(31,77,54,0.10)" },
  sensoriels: { icon: Sparkles, bg: "#FBEAD3", text: "#6B3F14", accent: "rgba(107,63,20,0.10)" },
  routine: { icon: Clock, bg: "#E6EEF5", text: "#1B5878", accent: "rgba(27,88,120,0.10)" },
};
const SESSION_CAT = { quiz: "cognition", comprendre: "cognition", puzzle: "motor", blindtest: "cognition" };

function BigButton({ onClick, title, sub, testId, bg, text, Icon, accent, image, round, familiar, scene }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      style={{ backgroundColor: bg, color: text }}
      className={`w-full min-h-[8rem] rounded-3xl p-5 sm:p-7 flex ${scene ? "flex-col sm:flex-row items-stretch sm:items-center" : "items-center"} gap-5 text-left active:scale-[0.98] transition-transform duration-200 border-4 border-black/5 shadow-sm hover:-translate-y-0.5 hover:shadow-[0_20px_40px_rgb(0,0,0,0.06)] transition-[transform,box-shadow]`}
    >
      {scene ? (
        <span className="shrink-0 w-full sm:w-48 aspect-[3/2] rounded-2xl overflow-hidden border-4 border-white/70 shadow-sm">
          <img src={scene} alt="" className="w-full h-full object-cover" data-testid={`${testId}-scene`} />
        </span>
      ) : image ? (
        <span className="relative shrink-0">
          <span className={`block w-20 h-20 ${round ? "rounded-full border-4" : "rounded-2xl border-2"} overflow-hidden border-white/60 bg-white/20`}>
            <img src={image} alt="" className="w-full h-full object-cover object-top" />
          </span>
          {familiar && <img src={familiar} alt="" className="absolute -bottom-1 -right-1 w-[45%] h-[45%] rounded-full object-cover border-2 border-white shadow-md" />}
        </span>
      ) : Icon ? (
        <span className="shrink-0 w-16 h-16 rounded-2xl flex items-center justify-center" style={{ backgroundColor: accent || "rgba(255,255,255,0.25)" }}>
          <Icon className="w-9 h-9" />
        </span>
      ) : null}
      <span className="flex-1 min-w-0">
        <span className="block font-patient font-black text-2xl sm:text-3xl">{title}</span>
        {sub && <span className="block font-patient font-medium text-lg sm:text-xl opacity-80 mt-1">{sub}</span>}
      </span>
      <ChevronRight className={`w-7 h-7 opacity-60 shrink-0 ${scene ? "hidden sm:block" : ""}`} />
    </button>
  );
}

export default function PatientApp() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [subActive, setSubActive] = useState(null);
  const exitTo = "/tableau-de-bord";
  const [categories, setCategories] = useState([]);
  const [games, setGames] = useState([]);

  const [view, setView] = useState("categories"); // categories | subcategories | games | play | success | note
  const [category, setCategory] = useState(null);
  const [subcategory, setSubcategory] = useState(null);
  const [playing, setPlaying] = useState(null); // { kind:'builtin'|'managed', id, name, sessionCat, game? }
  const [lastScore, setLastScore] = useState(0);
  const [newFamiliar, setNewFamiliar] = useState(null);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [message, setMessage] = useState(null);
  const [songs, setSongs] = useState([]);
  const [music, setMusic] = useState([]);
  const [fableQuiz, setFableQuiz] = useState(0);
  const [moraleQuiz, setMoraleQuiz] = useState(0);
  const [round, setRound] = useState(0);
  const [replying, setReplying] = useState(false);
  const [offline, setOffline] = useState(false);
  const [keptGames, setKeptGames] = useState(getKeptGames);
  const refreshKept = () => setKeptGames(getKeptGames());

  const load = useCallback(() => {
    api.get(`/patients/${patientId}`).then((r) => { setPatient(r.data); cachePatient(patientId, r.data); keepBravo(r.data.gardien_id || "leontine"); }).catch((e) => {
      const cached = !e.response && cachedPatient(patientId);
      if (!cached) { navigate("/tableau-de-bord"); return; }
      // Sans internet : on reprend le profil connu et les jeux gardés sur la tablette.
      setPatient(cached); setOffline(true); setSubActive(true); setView("kept");
    });
    api.get("/paypal/subscription").then((r) => {
      if (r.data.free && !r.data.paid) {
        toast.info("Le mode patient demande un abonnement. Votre accès offert concerne l'espace Aidant & Famille.");
        navigate("/famille", { replace: true });
        return;
      }
      if (r.data.trial_expired) {
        toast.info("Votre essai gratuit est terminé. Choisissez une formule pour continuer en mode patient.", { id: "trial-expired" });
        navigate("/abonnement", { replace: true });
        return;
      }
      setSubActive(!!r.data.active);
    }).catch((e) => setSubActive(!e.response && !!cachedPatient(patientId)));
    api.get("/game-categories").then((r) => setCategories(r.data)).catch(() => setCategories([]));
    api.get("/games").then((r) => setGames(r.data)).catch(() => setGames([]));
    api.get(`/patients/${patientId}/message`).then((r) => setMessage(r.data)).catch(() => {});
    api.get(`/patients/${patientId}/songs`).then((r) => setSongs(r.data)).catch(() => {});
    api.get("/music").then((r) => setMusic(r.data)).catch(() => {});
    api.get("/fables/quiz", { params: { n: 3 } }).then((r) => setFableQuiz(r.data.length)).catch(() => {});
    api.get("/fables/morale-quiz", { params: { n: 3 } }).then((r) => setMoraleQuiz(r.data.length)).catch(() => {});
  }, [patientId, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const sync = () => flushSessions().then((n) => n && toast.success(`${n} partie(s) jouée(s) sans internet envoyée(s) au suivi.`));
    sync();
    window.addEventListener("online", sync);
    return () => window.removeEventListener("online", sync);
  }, []);

  const gamesForSub = (cat, sub) => {
    const builtins = BUILTIN_GAMES
      .filter((b) => b.main_category === cat.id && b.sub_name === sub.name)
      .map((b) => ({ kind: "builtin", id: b.id, title: b.name, desc: b.desc }));
    const managed = games
      .filter((g) => g.main_category === cat.id && g.subcategory_id === sub.id)
      .map((g) => ({ kind: "managed", id: g.id, title: g.title, desc: g.description, game: g }));
    return [...builtins, ...managed];
  };

  const favMusic = music.filter((t) => (patient?.favorite_tracks || []).includes(t.id));
  const myMusic = favMusic.length ? favMusic : music;

  const handleComplete = async ({ score, accuracy, duration }) => {
    setLastScore(score);
    setNewFamiliar(null);
    if (playing) {
      const payload = {
        patient_id: patientId,
        category: playing.sessionCat || "cognition",
        exercise_id: playing.kind === "managed" ? `managed:${playing.id}` : playing.id,
        exercise_name: playing.name,
        score, accuracy, duration_seconds: duration,
      };
      try {
        const { data: saved } = await api.post("/sessions", payload);
        setNewFamiliar(saved.new_familiars?.[0] || null);
      } catch (e) {
        if (!e.response) { queueSession(payload); toast("Pas d'internet : le résultat est gardé et sera envoyé au suivi plus tard."); }
      }
    }
    setView("success");
  };

  const startGame = (item, fromKept = false) => {
    if (item.kind === "builtin") {
      setPlaying({ kind: "builtin", id: item.id, name: item.title, sessionCat: EXERCISE_META[item.id]?.category || "cognition", fromKept, gardienId: item.gardien_id });
    } else {
      setPlaying({ kind: "managed", id: item.id, name: item.title, sessionCat: SESSION_CAT[item.game.category] || "cognition", game: item.game, fromKept, gardienId: item.gardien_id });
    }
    setView("play");
  };

  const renderPlayer = () => {
    if (!playing) return null;
    if (playing.kind === "managed") {
      const g = playing.game;
      switch (g.category) {
        case "quiz":
        case "comprendre": return <QuizPlayer game={g} onComplete={handleComplete} />;
        case "puzzle": return <PuzzlePlayer game={g} onComplete={handleComplete} />;
        case "blindtest": return <BlindTestPlayer game={g} onComplete={handleComplete} />;
        case "pronunciation": return <PronunciationPlayer game={g} onComplete={handleComplete} patientId={patientId} />;
        case "breathing": return <BreathingPlayer onComplete={handleComplete} />;
        case "colors": return <ColorsPlayer onComplete={handleComplete} />;
        case "sounds": return <SoundsPlayer onComplete={handleComplete} />;
        default: return null;
      }
    }
    switch (playing.id) {
      case "memory": return <MemoryMatch onComplete={handleComplete} />;
      case "recognition": return <Recognition onComplete={handleComplete} />;
      case "dragmatch": return <DragMatch onComplete={handleComplete} />;
      case "taptargets": return <TapTargets onComplete={handleComplete} />;
      case "pathtrace": return <PathTrace onComplete={handleComplete} />;
      case "picwords": return <PicWords onComplete={handleComplete} />;
      case "crossword": return <Crossword onComplete={handleComplete} />;
      case "sudoku_facile": return <Sudoku level="facile" onComplete={handleComplete} />;
      case "sudoku_moyen": return <Sudoku level="moyen" onComplete={handleComplete} />;
      case "differences": return <Differences onComplete={handleComplete} />;
      default: return null;
    }
  };

  if (!patient || subActive === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2]">
        <Loader2 className="w-10 h-10 animate-spin text-emerald-700" />
      </div>
    );
  }

  if (subActive === false) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center p-6" data-testid="patient-locked">
        <div className="w-full max-w-md bg-white rounded-3xl border border-gray-200 shadow-sm p-8 text-center">
          <span className="mx-auto mb-5 w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center">
            <Lock className="w-8 h-8 text-amber-600" />
          </span>
          <h1 className="font-serif-display text-2xl font-normal text-gray-800 mb-2">Fonctionnalité réservée aux abonnés</h1>
          <p className="text-gray-500 mb-6">Les séances d'activités nécessitent un abonnement Passerelle actif.</p>
          <button onClick={() => navigate("/abonnement")} data-testid="patient-locked-subscribe"
            className="w-full h-12 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white font-semibold inline-flex items-center justify-center gap-2 transition-colors">
            <CreditCard className="w-5 h-5" /> Voir l'abonnement
          </button>
          <button onClick={() => navigate(exitTo)} data-testid="patient-locked-back"
            className="mt-3 text-sm text-gray-500 hover:text-emerald-700">Retour au tableau de bord</button>
        </div>
      </div>
    );
  }

  const HOME_GAMES = { family_songs: "songs", music_blindtest: "blindtest", fable_whosaid: "whosaid", fable_morale: "morale" };
  const afterGame = HOME_GAMES[playing?.id] ? "categories" : playing?.fromKept ? "kept" : "games";
  if (view === "success") {
    return <SuccessScreen newFamiliar={newFamiliar} gardienId={patient.gardien_id || playing?.gardienId || (category ? gardienForCategory(category.id).id : "leontine")} score={lastScore} onHome={() => setView(afterGame)} onAgain={() => { setRound((r) => r + 1); setView(HOME_GAMES[playing?.id] || "play"); }} onNote={() => setView("note")} />;
  }
  if (view === "note") {
    return (
      <div className="min-h-screen bg-[#FAF7F2] overflow-y-auto flex items-center justify-center p-6">
        <SessionNoteForm patientId={patientId} exerciseName={playing?.name || ""} onDone={() => setView(afterGame)} />
      </div>
    );
  }

  const goBack = () => {
    if (["songs", "familiars", "kept", "music", "blindtest", "whosaid", "morale"].includes(view)) setView("categories");
    else if (view === "play" && playing?.fromKept) setView("kept");
    else if (view === "play") setView("games");
    else if (view === "games") setView("subcategories");
    else if (view === "subcategories") setView("categories");
    else if (offline) setView("kept");
    else navigate(exitTo);
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] flex flex-col">
      <header className="flex items-center justify-between p-5 sm:p-7">
        <button onClick={goBack} data-testid="patient-back-button"
          className="flex items-center gap-2 h-16 px-6 rounded-2xl bg-white border-4 border-emerald-700/20 font-patient font-bold text-xl text-gray-700 active:scale-95 transition-transform duration-200">
          <ChevronLeft className="w-7 h-7" />
          {view === "categories" ? "Quitter" : "Retour"}
        </button>
        <div className="flex items-center gap-3">
          <button onClick={() => setVoiceOpen(true)} data-testid="patient-voice-settings-button"
            className="flex items-center gap-2 h-16 px-5 rounded-2xl bg-white border-4 border-emerald-700/20 font-patient font-bold text-lg text-gray-700 active:scale-95 transition-transform duration-200">
            <Settings2 className="w-6 h-6 text-emerald-700" />
            <span className="hidden sm:inline">Voix</span>
          </button>
          {patient.gardien_id && (
            <span className="hidden md:flex items-center gap-2 h-16 pl-1.5 pr-4 rounded-full bg-warm/10 border border-warm/30" data-testid="patient-header-gardien">
              <GardienAvatar id={patient.gardien_id} className="w-12 h-12" />
              <span className="font-patient font-bold text-lg text-gray-700">Avec {getGardien(patient.gardien_id).name}{getGardien(patient.gardien_id).familiar ? ` et ${getGardien(patient.gardien_id).familiar.name}` : ""}</span>
            </span>
          )}
          <span className="font-serif-display italic text-2xl text-gray-700 hidden sm:block">{patient.name}</span>
          {patient.avatar && <img src={patient.avatar} alt={patient.name} className="w-14 h-14 rounded-full object-cover border-4 border-amber-200" />}
        </div>
      </header>

      <PatientVoiceDialog patientId={patientId} patientName={patient.name} open={voiceOpen} onOpenChange={setVoiceOpen} />

      <main className="flex-1 flex flex-col items-center justify-center p-6 pb-16 overflow-y-auto">
        {view === "categories" && (
          <div className="w-full max-w-2xl soft-in" data-testid="patient-home">
            <ResidentPortrait name={patient.name} avatar={patient.avatar} gardienId={patient.gardien_id} className="mb-8" testId="patient-portrait" alive />
            <h1 className="text-4xl sm:text-5xl font-serif-display italic text-gray-800 mb-3 text-center">Bonjour {patient.name} !</h1>
            <p className="text-2xl font-patient text-gray-500 mb-8 text-center">Choisissez une catégorie de jeux.</p>
            {message && (
              <div className="rounded-3xl bg-rose-50 border-4 border-rose-200 p-6 mb-8 flex items-center gap-5" data-testid="patient-family-message">
                <MiniPlay url={message.audio_url} testId="patient-family-message-play" className="w-20 h-20 [&_svg]:w-9 [&_svg]:h-9" />
                <span>
                  <span className="flex items-center gap-2 font-patient font-black text-2xl text-gray-800"><HeartHandshake className="w-7 h-7 text-rose-400" /> Un mot doux de {message.label}</span>
                  <span className="block font-patient text-lg text-gray-500 mt-1">Appuyez pour écouter</span>
                </span>
              </div>
            )}
            {message && (replying ? (
              <VoiceRecorder postUrl={`/patients/${patientId}/reply`} hideName title={`Votre réponse à ${message.label}`}
                intro="Appuyez sur Commencer, parlez, puis Arrêter. Vous pourrez réécouter avant d'envoyer."
                onClose={() => setReplying(false)} onSaved={() => setReplying(false)} />
            ) : (
              <button onClick={() => setReplying(true)} data-testid="patient-reply-button"
                className="-mt-4 mb-8 w-full min-h-20 rounded-3xl bg-white border-4 border-rose-200 font-patient font-black text-2xl text-rose-500 inline-flex items-center justify-center gap-3 active:scale-[0.98] transition-transform">
                <Mic className="w-8 h-8" /> Répondre à {message.label}
              </button>
            ))}
            <GardienBadge id={patient.gardien_id || "leontine"} eyebrow={patient.gardien_id ? `${gardienLabel(getGardien(patient.gardien_id)).replace(/^(La|Le|Les) /, (m) => ({ "La ": "Votre ", "Le ": "Votre ", "Les ": "Vos " }[m]))}, ${patient.name}` : "Votre Gardienne du jour"} className="mb-10" testId="patient-home-gardien" />
            <div className="flex flex-col gap-6">
              {categories.map((c) => {
                const st = CAT_STYLE[c.id] || CAT_STYLE.alzheimer;
                return (
                  <BigButton key={c.id} testId={`patient-category-${c.id}`} title={c.label}
                    sub={`Avec ${gardienForCategory(c.id).name} · ${c.subcategories.length ? `${c.subcategories.length} sous-catégorie(s)` : "Contenu à venir"}`}
                    bg={st.bg} text={st.text} Icon={st.icon} accent={st.accent} image={gardienForCategory(c.id).image} familiar={gardienForCategory(c.id).familiar?.image} round scene={CATEGORY_SCENE(c.id)}
                    onClick={() => { setCategory(c); setView("subcategories"); }} />
                );
              })}
              {keptGames.length > 0 && (
                <BigButton testId="patient-kept-games-button" title="Jeux gardés sur la tablette"
                  sub={`${keptGames.length} jeu(x) jouable(s) même sans internet`}
                  bg="#E8F3EC" text="#1F5A3A" Icon={Download} accent="rgba(31,90,58,0.10)"
                  onClick={() => setView("kept")} />
              )}
              <BigButton testId="patient-stories-button" title="Contes & Poésie"
                sub="Avec Arsène · un moment de lecture douce"
                bg="#FFFFFF" text="#273643" Icon={BookOpen} image={gardienForCategory("contes").image} familiar={gardienForCategory("contes").familiar?.image} round
                onClick={() => navigate(`/contes?patient=${patientId}`)} />
              <BigButton testId="patient-familiars-button" title="L'album des familiers"
                sub="Un nouveau petit compagnon toutes les 5 activités"
                bg="#FFFFFF" text="#273643" Icon={Sparkles} accent="rgba(217,161,37,0.15)"
                image={getGardien(patient.gardien_id).familiar?.image || GARDIENS[0].familiar.image} round
                onClick={() => setView("familiars")} />
              {myMusic.length > 0 && (
                <BigButton testId="patient-music-button" title="Mes chansons"
                  sub={favMusic.length ? `${favMusic.length} chanson(s) que vous aimez` : `${music.length} chanson(s) à écouter`}
                  bg="#FDECEF" text="#7A2338" Icon={Headphones} accent="rgba(122,35,56,0.10)"
                  onClick={() => setView("music")} />
              )}
              {music.length >= 3 && (
                <BigButton testId="patient-blindtest-button" title="Blind test musical"
                  sub="Écoutez l'extrait et retrouvez la chanson"
                  bg="#FFFFFF" text="#273643" Icon={Disc3} accent="rgba(122,35,56,0.10)"
                  onClick={() => { setPlaying({ kind: "builtin", id: "music_blindtest", name: "Blind test musical", sessionCat: "cognition" }); setView("blindtest"); }} />
              )}
              {fableQuiz >= 3 && (
                <BigButton testId="patient-whosaid-button" title="Qui a dit quoi ?"
                  sub="Retrouvez l'animal de la fable qui a dit cette phrase"
                  bg="#FFF7E6" text="#78350F" Icon={Feather} accent="rgba(180,115,47,0.12)"
                  onClick={() => { setPlaying({ kind: "builtin", id: "fable_whosaid", name: "Qui a dit quoi ?", sessionCat: "cognition" }); setView("whosaid"); }} />
              )}
              {moraleQuiz >= 3 && (
                <BigButton testId="patient-morale-button" title="Complète la morale"
                  sub="Retrouvez la vraie fin de la morale de la fable"
                  bg="#FFF1E6" text="#7C2D12" Icon={ScrollText} accent="rgba(194,65,12,0.10)"
                  onClick={() => { setPlaying({ kind: "builtin", id: "fable_morale", name: "Complète la morale", sessionCat: "cognition" }); setView("morale"); }} />
              )}
              {songs.length > 0 && (
                <BigButton testId="patient-family-songs-button" title="Les chansons de la famille"
                  sub={`${songs.length} chanson(s) de vos proches, présentées par Gaston`}
                  bg="#FBEAD3" text="#6B3F14" Icon={Music} accent="rgba(107,63,20,0.10)"
                  onClick={() => { setPlaying({ kind: "builtin", id: "family_songs", name: "Chansons de la famille", sessionCat: "cognition" }); setView("songs"); }} />
              )}
            </div>
          </div>
        )}

        {view === "subcategories" && category && (
          <div className="w-full max-w-2xl soft-in" data-testid="patient-subcategories">
            <h1 className="text-4xl font-serif-display italic text-gray-800 mb-6 text-center">{category.label}</h1>
            <GardienBadge id={gardienForCategory(category.id).id} eyebrow="Votre Gardien vous accompagne" className="mb-8" testId="patient-category-gardien" />
            {category.subcategories.length === 0 ? (
              <div className="rounded-3xl bg-white border-4 border-dashed border-gray-300 p-12 text-center" data-testid="subcategories-empty">
                <p className="text-2xl font-patient font-bold text-gray-400">Contenu à venir</p>
              </div>
            ) : (
              <div className="flex flex-col gap-5">
                {category.subcategories.map((s) => {
                  const count = gamesForSub(category, s).length;
                  return (
                    <BigButton key={s.id} testId={`patient-subcategory-${s.id}`} title={s.name}
                      sub={count ? `${count} jeu(x)` : "Contenu à venir"}
                      bg="#FFFFFF" text="#273643" image={s.image}
                      onClick={() => { setSubcategory(s); setView("games"); }} />
                  );
                })}
              </div>
            )}
          </div>
        )}

        {view === "games" && category && subcategory && (
          <div className="w-full max-w-2xl soft-in" data-testid="patient-games">
            <h1 className="text-3xl sm:text-4xl font-serif-display italic text-gray-800 mb-8 text-center">{subcategory.name}</h1>
            {gamesForSub(category, subcategory).length === 0 ? (
              <div className="rounded-3xl bg-white border-4 border-dashed border-gray-300 p-12 text-center" data-testid="games-empty">
                <p className="text-2xl font-patient font-bold text-gray-400">Contenu à venir</p>
              </div>
            ) : (
              <div className="flex flex-col gap-5">
                {gamesForSub(category, subcategory).map((item) => (
                  <div key={`${item.kind}-${item.id}`} className="space-y-3">
                    <BigButton testId={`patient-game-${item.id}`} title={item.title}
                      sub={item.desc} bg="#FFFFFF" text="#273643" onClick={() => startGame(item)} />
                    <div className="flex flex-wrap gap-2">
                      <PrintGameButton game={item.kind === "managed" ? item.game : { id: item.id, title: item.title, builtin: true }} />
                      <KeepGameButton item={{ ...item, gardien_id: gardienForCategory(category.id).id }} onChange={refreshKept} />
                    </div>
                    {item.kind === "managed" && <GameRecommendation game={item.game} />}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {view === "kept" && (
          <div className="w-full max-w-2xl soft-in" data-testid="patient-kept-games">
            {offline && (
              <div className="rounded-2xl bg-amber-50 border-2 border-amber-200 p-4 mb-6 font-patient text-lg text-amber-900" data-testid="patient-offline-banner">
                Pas d'internet pour le moment : voici les jeux gardés sur la tablette. Les résultats seront envoyés au suivi au retour d'internet.
              </div>
            )}
            <h1 className="text-3xl sm:text-4xl font-serif-display italic text-gray-800 mb-8 text-center">Jeux gardés sur la tablette</h1>
            {keptGames.length === 0 ? (
              <div className="rounded-3xl bg-white border-4 border-dashed border-gray-300 p-12 text-center" data-testid="kept-games-empty">
                <p className="text-2xl font-patient font-bold text-gray-400">Aucun jeu gardé sur la tablette</p>
              </div>
            ) : (
              <div className="flex flex-col gap-5">
                {keptGames.map((item) => (
                  <div key={`${item.kind}-${item.id}`} className="space-y-3">
                    <BigButton testId={`patient-kept-game-${item.id}`} title={item.title} sub={item.desc}
                      bg="#FFFFFF" text="#273643" onClick={() => startGame(item, true)} />
                    <KeepGameButton item={item} onChange={refreshKept} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {view === "songs" && <FamilySongs songs={songs} onComplete={handleComplete} />}
        {view === "music" && <SimpleMusicPlayer tracks={myMusic} />}
        {view === "blindtest" && <BlindTest key={round} tracks={music} onComplete={handleComplete} />}
        {view === "whosaid" && <WhoSaid key={round} onComplete={handleComplete} />}
        {view === "morale" && <MoraleQuiz key={round} onComplete={handleComplete} />}
        {view === "familiars" && <FamiliarAlbum patientId={patientId} />}

        {view === "play" && (
          <div className="w-full soft-in" key={(playing?.id || "") + view}>
            {renderPlayer()}
            {!["family_songs"].includes(playing?.id) && playing?.game?.category !== "breathing" && (
              <HesitationHelper gardienId={patient.gardien_id || (category ? gardienForCategory(category.id).id : "leontine")} />
            )}
          </div>
        )}
      </main>
    </div>
  );
}
